import { useCallback } from 'react';
import {
  Colors,
  Flex,
  IconChevronRight,
  RemoteImage,
  Tag,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import type {
  FrequentlyVisitedMarketListProps,
  FrequentlyVisitedMarketEntity,
  FrequentlyVisitedMarketGoods,
} from './types';
import { bridge } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const FrequentlyVisitedMarketList = ({
  entity,
  logging,
}: FrequentlyVisitedMarketListProps) => {
  if (!entity || entity.length === 0) return null;

  return (
    <Flex>
      {entity.map(market => (
        <MarketCard
          key={market.item.market.item.sno}
          entity={market}
          logging={logging}
        />
      ))}
    </Flex>
  );
};

const MarketCard = ({
  entity,
  logging,
}: {
  entity: FrequentlyVisitedMarketEntity;
  logging: IScreensLogging;
}) => {
  const { market, goods } = entity.item;
  const { openDeeplink } = useOpenDeeplink();

  const handlePressMarket = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_MARKET',
      analytics: { ...logging?.analytics, ...market.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...market.logging?.ably_only_log },
    });
    openDeeplink(market.item.deeplink);
  }, [market, logging, openDeeplink]);

  return (
    <Flex
      margin={{ top: 4, horizontal: 16, bottom: 10 }}
      backgroundColor="white"
      borderRadius={6}
      overflow="hidden"
      shadowStep={'xlarge'}
    >
      {/* 상품 이미지 3개 */}
      <Flex.Row>
        {goods.slice(0, 3).map(goodsItem => (
          <GoodsThumb
            key={goodsItem.item.sno}
            goods={goodsItem}
            logging={logging}
          />
        ))}
      </Flex.Row>

      {/* 마켓 정보 */}
      <Flex.Row
        alignItems="center"
        padding={{ horizontal: 16, vertical: 12 }}
        onPress={handlePressMarket}
        gap={8}
      >
        <Flex
          width={45}
          height={45}
          borderRadius={22}
          overflow="hidden"
          borderWidth={1}
          borderColor="border_tertiary"
        >
          <RemoteImage
            uri={market.item.profile}
            width={45}
            height={45}
            resizeMode="cover"
          />
        </Flex>
        <Flex flex={1} gap={4}>
          <Flex.Row alignItems="center" gap={4}>
            <Tag color="gray" size="xsmall" type="secondary">
              {market.item.market_type.name}
            </Tag>
            <Typography
              type="subtitle2"
              color="content_primary"
              numberOfLines={1}
            >
              {market.item.name}
            </Typography>
          </Flex.Row>
          <Typography type="body4" color="content_secondary">
            최근 본 상품 {market.item.recently_viewed_goods_count}개
          </Typography>
        </Flex>
        <IconChevronRight size={20} fill={Colors.content_tertiary} />
      </Flex.Row>
    </Flex>
  );
};

const GoodsThumb = ({
  goods,
  logging,
}: {
  goods: FrequentlyVisitedMarketGoods;
  logging: IScreensLogging;
}) => {
  const { width: screenWidth } = useScreenSize();
  const thumbWidth = (screenWidth - 32) / 3;
  const thumbHeight = thumbWidth * 1.2;
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_GOODS',
      analytics: { ...logging?.analytics, ...goods.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...goods.logging?.ably_only_log },
    });
    openDeeplink(goods.item.deeplink);
  }, [goods, logging, openDeeplink]);

  return (
    <Flex flex={1} onPress={handlePress}>
      <RemoteImage
        uri={goods.item.thumbnail}
        width={thumbWidth}
        height={thumbHeight}
        resizeMode="cover"
      />
    </Flex>
  );
};
